import { z } from "zod";

import { MATERIAL_MAX_CHARS } from "@/utils/input-limits";

const FASTENER_BRAND_CODES = [
  "PEM",
  "SHEREX",
  "PROFIL",
  "AUTORIV",
  "OTHER",
] as const;

// PENN-087: annual_volume / batch_size are DB Decimal(15,4) — the largest
// storable value is 99,999,999,999.9999. A bigger number used to pass the form,
// reach the API, then overflow the column and crash with a generic 500. Cap it
// here (like the materialCode VARCHAR cap) so the user gets a clean, friendly
// inline message instead. The shared API schema enforces the same ceiling.
const DECIMAL_15_4_MAX = 99_999_999_999.9999;

// Required, strictly-positive numeric field. The value stays nullable in the
// type (so the form can start empty), but validation rejects a missing value
// and any value <= 0 — a zero/blank batch size or annual volume cannot drive
// the cost & utilisation calculation.
const requiredPositiveNumber = (label: string) =>
  z
    .union([z.literal(""), z.null(), z.coerce.number()])
    .transform((v) => (v === "" || v === null ? null : (v as number)))
    .superRefine((v, ctx) => {
      if (v == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required`,
        });
      } else if (v <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} must be greater than 0`,
        });
      } else if (v > DECIMAL_15_4_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is too large (must be 99,999,999,999 or less)`,
        });
      }
    });

const optionalString = z
  .string()
  .transform((v) => v.trim())
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional();

// Like `optionalString` but capped (DB column width). Material is VARCHAR(100)
// (penn_config.materials.material_code + applications.material_code); keep the
// client cap aligned so a long value is blocked here instead of 400/500-ing.
const optionalStringMax = (max: number) =>
  z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().max(max, `Must be ${max} characters or fewer`))
    .transform((v) => (v.length === 0 ? null : v))
    .nullable()
    .optional();

const qtyField = z
  .union([
    z.literal(""),
    z.coerce.number().nonnegative("Qty cannot be negative"),
  ])
  .transform((v) => (v === "" ? 0 : (v as number)));

const fastenerSchema = z.object({
  fastenerId: z.string().optional(),
  brand: z.enum(FASTENER_BRAND_CODES, { message: "Pick a brand" }),
  type: optionalString,
  qtyPerPartA: qtyField,
  qtyPerPartB: qtyField,
});

export const applicationFormSchema = z.object({
  name: z.string().trim().min(1, "Application name is required").max(100),
  applicationNumber: optionalString,
  oem: optionalString,
  productionProgram: optionalString,
  materialCode: optionalStringMax(MATERIAL_MAX_CHARS),
  annualVolume: requiredPositiveNumber("Annual production volume"),
  batchSize: requiredPositiveNumber("Average batch size"),
  fasteners: z.array(fastenerSchema),
});

export type ApplicationFormData = z.infer<typeof applicationFormSchema>;
export type ApplicationFastenerForm = z.infer<typeof fastenerSchema>;
